import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { PRICING_PLANS } from "../lib/site";
import { Seo } from "../components/Seo";
import { SlotPicker } from "../components/SlotPicker";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";

const RATE_SECTIONS = {
  clarity: { title: "Brand Clarity", items: ["People immediately understand what we do.", "Our ideal customer is clearly defined.", "Our messaging feels specific rather than generic.", "Our online presence supports our pricing."] },
  authority: { title: "Digital Authority", items: ["Our website feels professional and current.", "Our site establishes trust quickly.", "We display testimonials, case studies, results, or proof.", "Our brand feels consistent across platforms."] },
  conversion: { title: "Conversion", items: ["Our website has a clear primary CTA.", "Visitors know what to do next.", "Our service pages explain value clearly.", "We capture leads who are not ready to purchase."] },
  acquisition: { title: "Client Acquisition", items: ["We have a predictable source of new inquiries.", "We follow up consistently.", "We have an email list or nurture system.", "We have a repeatable acquisition system."] },
};
const BIZ_TYPES = ["Solo professional / personal brand", "Creative professional", "Service business", "Agency / studio", "Entertainment business", "Local business", "E-commerce brand", "Professional services", "Other"];
const STAGES = ["Just getting established", "Established but inconsistent", "Growing through referrals", "Getting attention but not enough inquiries", "Established but brand feels outdated", "Website does not reflect current business", "Ready to increase pricing", "Ready to scale", "Rebranding or repositioning"];
const CHANNELS = ["Referrals", "Instagram", "TikTok", "Google", "SEO", "Paid advertising", "Networking", "Cold outreach", "Partnerships", "Repeat customers", "Marketplaces", "Other"];
const GOALS = ["Get more clients", "Increase pricing", "Look more established", "Improve conversions", "Rebrand", "Launch a new offer", "Create predictable lead generation", "Improve customer experience", "Scale", "Other"];
const TIMELINES = ["Immediately", "Within 30 days", "1-3 months", "3-6 months", "Later / researching"];

const getUtm = () => {
  const p = new URLSearchParams(window.location.search);
  return { source: p.get("utm_source") || "", medium: p.get("utm_medium") || "", campaign: p.get("utm_campaign") || "" };
};

const Pill = ({ active, children, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`w-full rounded-xl border px-4 py-3.5 text-left text-sm font-semibold transition-colors ${active ? "border-[#C6FF00] bg-[#C6FF00]/10 text-[#C6FF00]" : "border-[#23262c] bg-[#14161b] text-[#c8cdd3] hover:border-[#3a3f47]"}`}>
    {children}
  </button>
);

const Rate = ({ value, onChange, testid }) => (
  <div className="flex gap-2">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" data-testid={`${testid}-${n}`} onClick={() => onChange(n)}
        className={`h-11 w-11 rounded-full border text-sm font-bold transition-colors ${value === n ? "border-[#C6FF00] bg-[#C6FF00] text-[#0A0C0F]" : "border-[#23262c] bg-[#14161b] text-[#c8cdd3] hover:border-[#3a3f47]"}`}>
        {n}
      </button>
    ))}
  </div>
);

const Input = ({ label, value, onChange, type = "text", testid, required }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#8A939B]">{label}{required && <span className="text-[#C6FF00]"> *</span>}</span>
    <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-[#23262c] bg-[#14161b] px-4 py-3 text-white outline-none focus:border-[#C6FF00]" />
  </label>
);

export default function BrandScore() {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [leadId, setLeadId] = useState(null);
  const [booking, setBooking] = useState(false);
  const [slots, setSlots] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booked, setBooked] = useState(null);
  const [busy, setBusy] = useState(false);
  const topRef = useRef(null);
  const [f, setF] = useState({
    first_name: "", last_name: "", business_name: "", email: "", website: "", instagram: "", other_social: "", industry: "",
    business_type: "", business_stage: "",
    ratings: { clarity: Array(4).fill(0), authority: Array(4).fill(0), conversion: Array(4).fill(0), acquisition: Array(4).fill(0) },
    channels: [], goal: "", obstacle: "", timeline: "", budget: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setRating = (sec, i, v) => setF((p) => { const r = { ...p.ratings, [sec]: [...p.ratings[sec]] }; r[sec][i] = v; return { ...p, ratings: r }; });

  const budgets = useMemo(() => {
    const prices = PRICING_PLANS.map((p) => p.price);
    const opts = prices.map((p, i) => (i === 0 ? `Around ${p}` : `${prices[i - 1]} – ${p}`));
    return [...opts, `${prices[prices.length - 1]}+`, "I'm not sure yet"];
  }, []);

  useEffect(() => { api.post("/acquisition/events", { event: "brand_score_page_view", utm: getUtm(), landing_page: "/brand-score" }).catch(() => {}); }, []);
  useEffect(() => {
    const rid = new URLSearchParams(window.location.search).get("resume");
    if (rid) { setLeadId(rid); setStarted(true); setBooking(true); }
  }, []);
  useEffect(() => { if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, [step, booking]);
  useEffect(() => {
    if (booking) api.get("/acquisition/slots").then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [booking]);

  const start = () => {
    setStarted(true);
    api.post("/acquisition/events", { event: "assessment_started", utm: getUtm() }).catch(() => {});
  };

  const rateSecs = Object.keys(RATE_SECTIONS);
  // steps: 0 profile, 1 stage, 2 clarity, 3 authority, 4 conversion, 5 channels+acquisition, 6 goals, 7 timeline+budget
  const TOTAL = 8;
  const valid = () => {
    if (step === 0) return f.first_name && f.last_name && f.business_name && /.+@.+\..+/.test(f.email);
    if (step === 1) return !!f.business_stage;
    if (step === 2) return f.ratings.clarity.every(Boolean);
    if (step === 3) return f.ratings.authority.every(Boolean);
    if (step === 4) return f.ratings.conversion.every(Boolean);
    if (step === 5) return f.channels.length > 0 && f.ratings.acquisition.every(Boolean);
    if (step === 6) return !!f.goal;
    if (step === 7) return !!f.timeline && !!f.budget;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/acquisition/submit", { ...f, utm: getUtm(), landing_page: "/brand-score" });
      setLeadId(res.data.lead_id);
      setBooking(true);
      api.post("/acquisition/events", { event: "call_booking_started", lead_id: res.data.lead_id, utm: getUtm() }).catch(() => {});
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const bookSlot = async () => {
    setBusy(true);
    try {
      const res = await api.post("/acquisition/book", { lead_id: leadId, slot_id: selectedSlot });
      setBooked(res.data.call);
    } catch (e) {
      toast.error(apiError(e));
      if (e?.response?.status === 409) {
        const r = await api.get("/acquisition/slots").catch(() => null);
        if (r) setSlots(r.data.slots);
        setSelectedSlot(null);
      }
    } finally { setBusy(false); }
  };

  const rateStep = (sec) => (
    <div className="space-y-5">
      <p className="text-sm text-[#8A939B]">Rate each statement from 1 (not true) to 5 (completely true).</p>
      {RATE_SECTIONS[sec].items.map((it, i) => (
        <div key={i} className="rounded-2xl border border-[#1c1f25] bg-[#101216] p-4">
          <p className="mb-3 text-sm font-semibold text-white">{it}</p>
          <Rate value={f.ratings[sec][i]} onChange={(v) => setRating(sec, i, v)} testid={`rate-${sec}-${i}`} />
        </div>
      ))}
    </div>
  );

  const steps = [
    { title: "Your Business Profile", body: (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input label="First name" required value={f.first_name} onChange={(v) => set("first_name", v)} testid="bs-first-name" />
        <Input label="Last name" required value={f.last_name} onChange={(v) => set("last_name", v)} testid="bs-last-name" />
        <Input label="Business name" required value={f.business_name} onChange={(v) => set("business_name", v)} testid="bs-business" />
        <Input label="Email" required type="email" value={f.email} onChange={(v) => set("email", v)} testid="bs-email" />
        <Input label="Website" value={f.website} onChange={(v) => set("website", v)} testid="bs-website" />
        <Input label="Instagram" value={f.instagram} onChange={(v) => set("instagram", v)} testid="bs-instagram" />
        <Input label="Other social URL" value={f.other_social} onChange={(v) => set("other_social", v)} testid="bs-other-social" />
        <Input label="Industry" value={f.industry} onChange={(v) => set("industry", v)} testid="bs-industry" />
        <div className="sm:col-span-2">
          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#8A939B]">Which best describes your business?</span>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {BIZ_TYPES.map((t) => <Pill key={t} testid={`bs-type-${t.slice(0, 8)}`} active={f.business_type === t} onClick={() => set("business_type", t)}>{t}</Pill>)}
          </div>
        </div>
      </div>
    )},
    { title: "Your Business Today", body: (
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {STAGES.map((s) => <Pill key={s} testid={`bs-stage-${s.slice(0, 10)}`} active={f.business_stage === s} onClick={() => set("business_stage", s)}>{s}</Pill>)}
      </div>
    )},
    { title: "Brand Clarity", body: rateStep("clarity") },
    { title: "Digital Authority", body: rateStep("authority") },
    { title: "Conversion", body: rateStep("conversion") },
    { title: "Client Acquisition", body: (
      <div className="space-y-6">
        <div>
          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#8A939B]">Where do most clients currently come from? (select all that apply)</span>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {CHANNELS.map((c) => <Pill key={c} testid={`bs-ch-${c.slice(0, 8)}`} active={f.channels.includes(c)} onClick={() => set("channels", f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c])}>{c}</Pill>)}
          </div>
        </div>
        {rateStep("acquisition")}
      </div>
    )},
    { title: "Your Goals", body: (
      <div className="space-y-6">
        <div>
          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#8A939B]">What is the biggest thing you want to accomplish in the next 6–12 months?</span>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {GOALS.map((g) => <Pill key={g} testid={`bs-goal-${g.slice(0, 10)}`} active={f.goal === g} onClick={() => set("goal", g)}>{g}</Pill>)}
          </div>
        </div>
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-[#8A939B]">What feels like the biggest thing standing between you and that goal?</span>
          <textarea data-testid="bs-obstacle" value={f.obstacle} onChange={(e) => set("obstacle", e.target.value)} rows={4}
            className="w-full rounded-xl border border-[#23262c] bg-[#14161b] px-4 py-3 text-white outline-none focus:border-[#C6FF00]" />
        </label>
      </div>
    )},
    { title: "Project Readiness", body: (
      <div className="space-y-6">
        <div>
          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#8A939B]">If HughLaunch identifies clear opportunities, when would you ideally want to address them?</span>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {TIMELINES.map((t) => <Pill key={t} testid={`bs-time-${t.slice(0, 8)}`} active={f.timeline === t} onClick={() => set("timeline", t)}>{t}</Pill>)}
          </div>
        </div>
        <div>
          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-[#8A939B]">What level of investment are you comfortable considering?</span>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {budgets.map((b) => <Pill key={b} testid={`bs-budget-${b.slice(0, 8)}`} active={f.budget === b} onClick={() => set("budget", b)}>{b}</Pill>)}
          </div>
        </div>
      </div>
    )},
  ];

  return (
    <div ref={topRef} className="min-h-screen bg-[#0A0C0F] text-white">
      <Seo title="HughLaunch Brand Score™ — Brand & Growth Diagnostic" description="How strong is the brand your customers actually experience? Complete the HughLaunch diagnostic and receive your strategist-reviewed Brand Score within 24 hours." path="/brand-score" />
      <div className="mx-auto max-w-3xl px-5 pt-6">
        <a href="/" data-testid="bs-home-logo" className="inline-flex items-center gap-2">
          <img src="/hl-logo-v2.png" alt="HughLaunch" className="h-8 w-auto" />
          <span className="text-lg font-extrabold">Hugh<span className="text-[#C6FF00]">Launch</span></span>
        </a>
      </div>
      <div className="mx-auto max-w-3xl px-5 py-14 lg:py-20">
        {booked ? (
          <div className="text-center" data-testid="bs-confirmed">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#C6FF00]" />
            <h1 className="mt-6 text-3xl font-extrabold sm:text-4xl">Your HughLaunch Audit is confirmed.</h1>
            <p className="mx-auto mt-4 max-w-lg text-[#8A939B]">Your Audit Review Call is booked for <span className="font-bold text-white">{booked.start_display}</span>. Our strategist review has begun — your HughLaunch Brand Score™ and audit findings will be ready within 24 hours.</p>
            {booked.meet_link && (
              <a href={booked.meet_link} target="_blank" rel="noreferrer" data-testid="bs-meet-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#C6FF00] px-7 py-3.5 font-extrabold text-[#C6FF00] transition-colors hover:bg-[#C6FF00] hover:text-[#0A0C0F]">
                Join Google Meet
              </a>
            )}
            <p className="mt-6 text-sm text-[#8A939B]">A confirmation email and a Google Calendar invite with your Google Meet link are on their way to your inbox.</p>
          </div>
        ) : booking ? (
          <div data-testid="bs-booking">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C6FF00]">One last step</p>
            <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">Schedule your Audit Review</h1>
            <p className="mt-4 text-[#8A939B]">Pick a time below for your Audit Review Call — we meet on Google Meet. Booking your call activates the 24-hour strategist review of your HughLaunch Brand Score™.</p>
            <div className="mt-8">
              <SlotPicker slots={slots} selected={selectedSlot} onSelect={setSelectedSlot} testPrefix="bs-slot" />
            </div>
            <button data-testid="bs-book-slot-btn" disabled={!selectedSlot || busy} onClick={bookSlot}
              className="mt-8 w-full rounded-full bg-[#C6FF00] px-7 py-4 font-extrabold text-[#0A0C0F] transition-transform hover:scale-[1.01] disabled:opacity-30">
              {busy ? "Booking…" : <>Book My Audit Review Call <ArrowRight className="ml-1 inline h-4 w-4" /></>}
            </button>
            <p className="mt-4 text-center text-xs text-[#5b636b]">All times Pacific · 30 minutes · Google Meet. Your audit activates the moment your call is booked — that's when our 24-hour strategist review begins.</p>
          </div>
        ) : !started ? (
          <div data-testid="bs-landing">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C6FF00]">HughLaunch Brand Score™</p>
            <h1 className="mt-4 text-4xl font-extrabold leading-tight sm:text-5xl">How strong is the brand your customers actually experience?</h1>
            <p className="mt-6 text-lg text-[#8A939B]">Your business may be better than your brand makes it look. The HughLaunch Brand Score identifies the gaps between the quality of your business and the way your market currently experiences your brand, website, trust, conversion path, and client acquisition system.</p>
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {["Complete the diagnostic", "Schedule your Audit Review Call", "Strategist-reviewed score in 24 hours"].map((s, i) => (
                <div key={i} className="rounded-2xl border border-[#1c1f25] bg-[#101216] p-4">
                  <span className="text-xs font-bold text-[#C6FF00]">STEP {i + 1}</span>
                  <p className="mt-1 text-sm font-semibold text-white">{s}</p>
                </div>
              ))}
            </div>
            <button data-testid="bs-start-btn" onClick={start} className="mt-8 rounded-full bg-[#C6FF00] px-8 py-4 text-base font-extrabold text-[#0A0C0F] transition-transform hover:scale-[1.02]">
              GET MY HUGHLAUNCH BRAND SCORE <ArrowRight className="ml-1 inline h-5 w-5" />
            </button>
            <p className="mt-4 text-xs text-[#5b636b]">Takes about 3 minutes. Complete the diagnostic, schedule your required Audit Review Call, and receive your strategist-reviewed score and findings within 24 hours.</p>
            <div className="mt-8 rounded-2xl border border-[#1c1f25] bg-[#101216] p-6" data-testid="bs-whats-included">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C6FF00]">What your Brand Score includes</p>
              <ul className="mt-4 space-y-3">
                {[
                  "Your overall Brand Score across 5 growth categories — clarity, authority, conversion, acquisition, infrastructure",
                  "Strategist-written audit findings on where your brand is losing opportunities",
                  "A prioritized fix list — what to address first for the fastest impact",
                  "A 1-on-1 Audit Review Call to walk through everything with a strategist",
                ].map((it, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#c8cdd3]">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#C6FF00]" /> {it}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8 rounded-2xl border border-[#1c1f25] bg-[#101216] p-6" data-testid="bs-what-it-measures">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C6FF00]">What the Brand Score measures</p>
              <p className="mt-3 text-sm leading-relaxed text-[#8A939B]">The HughLaunch Brand Score™ is a strategist-reviewed diagnostic of the core systems every growing business depends on. It's not a vanity quiz — each category maps directly to a stage of the H.U.G.H. Method™, the framework we use to take businesses from invisible to fully booked.</p>
              <div className="mt-4 space-y-3">
                {[
                  ["Brand Clarity", "Do people immediately understand what you do, who it's for, and why you're worth your price? Unclear brands lose customers before the first conversation ever happens."],
                  ["Digital Authority", "Does your website look and feel like the leader in your market? Trust is decided in seconds — an outdated design quietly rules you out."],
                  ["Conversion", "When someone lands on your site, is the next step obvious? Strong traffic with a weak conversion path is the most common — and most fixable — leak we find."],
                  ["Client Acquisition", "Do new enquiries arrive predictably, or only through referrals? A repeatable acquisition system is what separates growth from luck."],
                ].map(([t, d]) => (
                  <div key={t}>
                    <p className="text-sm font-bold text-white">{t}</p>
                    <p className="text-sm leading-relaxed text-[#8A939B]">{d}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-[#8A939B]">Every submission is reviewed by a human strategist — never auto-scored — and returned within 24 hours with written findings and a prioritized fix list you can act on immediately, whether or not you ever work with us.</p>
            </div>
          </div>
        ) : (
          <div data-testid={`bs-step-${step}`}>
            <div className="mb-8">
              <div className="flex items-center justify-between text-xs font-bold text-[#8A939B]">
                <span>SECTION {step + 1} OF {TOTAL}</span><span>{Math.round(((step) / TOTAL) * 100)}%</span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#1c1f25]">
                <div className="h-full rounded-full bg-[#C6FF00] transition-all" style={{ width: `${(step / TOTAL) * 100}%` }} />
              </div>
            </div>
            <h2 className="text-2xl font-extrabold">{steps[step].title}</h2>
            <div className="mt-6">{steps[step].body}</div>
            <div className="mt-10 flex items-center justify-between">
              <button data-testid="bs-back-btn" onClick={() => (step === 0 ? setStarted(false) : setStep(step - 1))} className="flex items-center gap-1 text-sm font-bold text-[#8A939B] hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
              {step < TOTAL - 1 ? (
                <button data-testid="bs-next-btn" disabled={!valid()} onClick={() => setStep(step + 1)}
                  className="rounded-full bg-[#C6FF00] px-7 py-3.5 font-extrabold text-[#0A0C0F] disabled:opacity-30">Continue <ArrowRight className="ml-1 inline h-4 w-4" /></button>
              ) : (
                <button data-testid="bs-submit-btn" disabled={!valid() || busy} onClick={submit}
                  className="rounded-full bg-[#C6FF00] px-7 py-3.5 font-extrabold text-[#0A0C0F] disabled:opacity-30">{busy ? "Submitting…" : "Submit My Diagnostic"}</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
